import { hasAskInputQuery } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hasAskInputQuery.js';
import { useAskInputWindowsVoiceUrlIntents } from '#pplx/frontend/apps/ask/ask-input/multimodal-url-intents/useAskInputUrlIntents.js';
import { useAskComposerTranscriptionImplementation } from '#pplx/frontend/apps/ask/ask-input/voice-controls/askComposerTranscriptionImplementationShared.js';
import { submitAskInputDictation } from '#pplx/frontend/apps/ask/ask-input/voice-controls/submitAskInputDictation.js';
import { useAskInputNativeVoiceEvents } from '#pplx/frontend/apps/ask/ask-input/voice-controls/useAskInputNativeVoiceEvents.js';
import { useAskInputTranscription } from '#pplx/frontend/apps/ask/ask-input/voice-controls/useAskInputTranscription.js';
import { useOpenVoiceToVoiceModal } from '#pplx/frontend/apps/ask/ask-input/voice-controls/useOpenVoiceToVoiceModal.js';
import { createAskDictationControlPlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/dictation/askDictationControlPlugin.js';
import { useWebVoiceContext } from '#pplx/frontend/apps/ask/multimodal/voice/launcher/WebVoiceContext.js';
import { usePerplexityQueryState } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useControlledDictationPlugins } from '#pplx/frontend/composer-sdk-plugins/dictation/index.js';
import { dictationMessages } from '#pplx/frontend/composer-sdk-plugins/dictation/messages.js';
import { serializedStateToPlainText } from '#pplx/frontend/composer-sdk/serializedStateToPlainText.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
/** Registers Ask dictation with caller-owned submission policy. */
export function useAskComposerDictationPlugins({ attachments, composerValue, disableActionButtons = false, isSubmitDisabled, onDictationSubmit, showStopButton, value, }) {
    const { $t } = useIntl();
    const isVoiceActive = useWebVoiceContext()?.isActive ?? false;
    const { quote } = usePerplexityQueryState();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const [focusRequest, setFocusRequest] = useState(0);
    const requestFocus = useCallback(() => setFocusRequest((request) => request + 1), []);
    const useTranscription = useAskComposerTranscriptionImplementation() ?? useAskInputTranscription;
    const { isInitializing, isSupported, isTranscribing, startTranscription, stopTranscription, transcription, } = useTranscription({
        onStart: requestFocus,
        onStop: requestFocus,
        reason: attachments.reason,
    });
    const openVoiceToVoiceModal = useOpenVoiceToVoiceModal();
    useAskInputNativeVoiceEvents({
        isTranscribing,
        isTranscriptionAvailable: isSupported,
        onStartTranscription: startTranscription,
        onStopTranscription: stopTranscription,
        onOpenVoiceToVoice: openVoiceToVoiceModal,
    });
    useAskInputWindowsVoiceUrlIntents({
        isWindowsApp,
        onStartDictation: startTranscription,
        onOpenVoiceToVoice: openVoiceToVoiceModal,
    });
    const hasQuery = hasAskInputQuery({
        attachments: attachments.attachmentUrls,
        hasPendingAttachments: attachments.isUploading,
        quote,
        value,
    });
    const handleDictationSubmit = useCallback(() => {
        submitAskInputDictation({
            hasQuery,
            onStopTranscription: stopTranscription,
            onSubmit: () => {
                if (isSubmitDisabled)
                    return;
                onDictationSubmit({
                    state: composerValue,
                    plainText: serializedStateToPlainText(composerValue),
                });
            },
        });
    }, [composerValue, hasQuery, isSubmitDisabled, onDictationSubmit, stopTranscription]);
    const startLabel = $t(dictationMessages.start);
    const stopLabel = $t(dictationMessages.stop);
    const [syncPlugin, , submitPlugin] = useControlledDictationPlugins({
        focusRequest,
        isInitializing,
        isSubmitDisabled,
        isSupported,
        isTranscribing,
        onStartTranscription: startTranscription,
        onStopTranscription: stopTranscription,
        onSubmit: handleDictationSubmit,
        startLabel,
        stopLabel,
        stopOnSubmitGesture: false,
        submitLabel: $t(dictationMessages.submit),
        transcription,
    });
    const controlPlugin = useMemo(() => createAskDictationControlPlugin({
        isDisabled: isVoiceActive,
        isInitializing,
        isSupported,
        isTranscribing,
        onStartTranscription: startTranscription,
        onStopTranscription: stopTranscription,
        startLabel,
        stopLabel,
    }), [
        isVoiceActive,
        isInitializing,
        isSupported,
        isTranscribing,
        startTranscription,
        stopTranscription,
        startLabel,
        stopLabel,
    ]);
    const showDictationControl = !disableActionButtons && (!showStopButton || isTranscribing);
    const plugins = useMemo(() => [
        syncPlugin,
        ...(showDictationControl ? [controlPlugin] : []),
        ...(!disableActionButtons ? [submitPlugin] : []),
    ], [controlPlugin, disableActionButtons, showDictationControl, submitPlugin, syncPlugin]);
    const stopTranscriptionIfActive = useCallback(() => {
        if (isTranscribing)
            stopTranscription();
    }, [isTranscribing, stopTranscription]);
    return {
        plugins,
        hasQuery,
        isTranscribing,
        placeholder: isTranscribing ? $t(dictationMessages.listening) : undefined,
        stopTranscriptionIfActive,
    };
}
//# sourceMappingURL=useAskComposerDictationPlugins.js.map